import { useEffect, useRef, useState, type MouseEvent } from "react";
import { Link, useLocation } from "react-router-dom";

interface MegaItem {
  label: string;
  description: string;
  icon: string;
  to: string;
}

interface MegaGroup {
  title: string;
  items: MegaItem[];
}

const MEGA_GROUPS: MegaGroup[] = [
  {
    title: "Criar e vender",
    items: [
      {
        label: "Criação de Sites",
        description: "Sites profissionais e responsivos.",
        icon: "ri-layout-masonry-line",
        to: "/sites",
      },
      {
        label: "Criação de Logotipo",
        description: "Marca e identidade visual.",
        icon: "ri-palette-line",
        to: "/sites#logotipo",
      },
      {
        label: "Lojas Virtuais",
        description: "Catálogo, carrinho e checkout.",
        icon: "ri-shopping-bag-3-line",
        to: "/lojas-virtuais",
      },
      {
        label: "Sistemas Web",
        description: "Plataformas sob medida.",
        icon: "ri-apps-2-line",
        to: "/sistemas",
      },
      {
        label: "Domínio e E-mail",
        description: "Identidade profissional.",
        icon: "ri-mail-send-line",
        to: "/dominios",
      },
    ],
  },
  {
    title: "Infraestrutura",
    items: [
      {
        label: "Hospedagem",
        description: "Estrutura estável e segura.",
        icon: "ri-server-line",
        to: "/hospedagem",
      },
      {
        label: "VPS",
        description: "Mais controle e recursos.",
        icon: "ri-hard-drive-3-line",
        to: "/vps",
      },
      {
        label: "Servidores",
        description: "Infraestrutura dedicada.",
        icon: "ri-database-2-line",
        to: "/servidores",
      },
      {
        label: "Revenda",
        description: "Monte seu negócio de hospedagem.",
        icon: "ri-store-2-line",
        to: "/revenda",
      },
    ],
  },
  {
    title: "Aparecer e crescer",
    items: [
      {
        label: "SEO",
        description: "Mais visibilidade no Google.",
        icon: "ri-search-eye-line",
        to: "/solucoes#seo",
      },
      {
        label: "Google Ads",
        description: "Anúncios para quem já procura.",
        icon: "ri-google-fill",
        to: "/solucoes#googleads",
      },
      {
        label: "Marketing Digital",
        description: "Estratégia e captação de leads.",
        icon: "ri-megaphone-line",
        to: "/solucoes#marketing",
      },
      {
        label: "Landing Pages",
        description: "Páginas focadas em conversão.",
        icon: "ri-pages-line",
        to: "/solucoes#landing",
      },
    ],
  },
];

interface SolucoesMegaMenuProps {
  solid: boolean;
}

export default function SolucoesMegaMenu({ solid }: SolucoesMegaMenuProps) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const onPointerDown = (event: globalThis.MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  const handleItemClick = (to: string) => (event: MouseEvent<HTMLAnchorElement>) => {
    setOpen(false);
    const hashIndex = to.indexOf("#");
    if (hashIndex === -1) return;
    const path = to.slice(0, hashIndex);
    const hash = to.slice(hashIndex + 1);
    if (location.pathname !== path) return;
    const el = document.getElementById(hash);
    if (!el) return;
    event.preventDefault();
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    window.history.replaceState(null, "", `${path}${window.location.search}#${hash}`);
  };

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="true"
        className={`inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-background-50 transition-colors ${
          solid ? "bg-primary-500 hover:bg-primary-600" : "bg-primary-500/90 hover:bg-primary-600"
        }`}
      >
        <i className="ri-sparkling-2-line text-base" aria-hidden="true"></i>
        Soluções
        <i
          className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
          aria-hidden="true"
        ></i>
      </button>

      <div
        className={`absolute left-0 top-full z-50 mt-3 w-[44rem] origin-top-left rounded-xl border border-background-200 bg-background-50 p-6 transition-all duration-200 ${
          open ? "visible scale-100 opacity-100" : "invisible -translate-y-1 scale-[0.98] opacity-0"
        }`}
        role="menu"
        aria-label="Soluções digitais"
      >
        <div className="grid grid-cols-1 gap-x-6 gap-y-7 sm:grid-cols-3">
          {MEGA_GROUPS.map((group) => (
            <div key={group.title}>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-400">
                {group.title}
              </p>
              <ul className="mt-3 flex flex-col gap-1">
                {group.items.map((item) => (
                  <li key={item.label}>
                    <Link
                      to={item.to}
                      onClick={handleItemClick(item.to)}
                      className="group flex items-start gap-3 rounded-lg p-2 transition-colors hover:bg-background-100"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                        <i className={`${item.icon} text-lg`} aria-hidden="true"></i>
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-foreground-950">
                          {item.label}
                        </span>
                        <span className="mt-0.5 block text-xs leading-snug text-foreground-500">
                          {item.description}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-5">
          <p className="text-xs text-foreground-500">
            Sites, hospedagem, VPS, SEO e marketing em um só lugar.
          </p>
          <Link
            to="/solucoes"
            onClick={() => setOpen(false)}
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-background-50 transition-colors hover:bg-primary-600"
          >
            Ver todas as soluções
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>
      </div>
    </div>
  );
}